/**
 * FooterSection — column headings + 2 link lists + newsletter + social URLs.
 * Each link row has a bilingual label + a single URL (the URL is the same
 * for both languages by default).
 *
 * The "Legal" column links and the copyright/company line are code-owned
 * (config/brand.js) so they always match the trade licence; only the Legal
 * column heading is editable here.
 *
 * Paths:
 *   footer.columns.{links, legal, resources}
 *   footer.linksItems[i].{label, href}
 *   footer.resourcesItems[i].{label, href, external?}
 *   footer.newsletter.{title, subtitle, placeholder, submit}
 *
 * Social URLs go under brand.{facebookUrl, twitterUrl, instagramUrl, linkedinUrl}.
 */

import {
  BilingualTextField, SectionPanel, UrlField, ItemListEditor,
} from '../fields/BilingualFields'

function LinkListEditor({ title, listKey, getField, setField, draftEn, draftAr, setSubtree, allowExternal = false }) {
  const itemsEn = (draftEn?.footer?.[listKey]) || []
  const itemsAr = (draftAr?.footer?.[listKey]) || []

  const onChange = (newEn, newAr) => {
    setSubtree('en', `footer.${listKey}`, newEn)
    setSubtree('ar', `footer.${listKey}`, newAr)
  }

  const updateHref = (idx, val) => {
    // Most footer links share a URL across languages.  Keep them in lockstep.
    const en = itemsEn.slice()
    const ar = itemsAr.slice()
    if (en[idx]) en[idx] = { ...en[idx], href: val }
    if (ar[idx]) ar[idx] = { ...ar[idx], href: val }
    onChange(en, ar)
  }

  const toggleExternal = (idx, val) => {
    const en = itemsEn.slice()
    const ar = itemsAr.slice()
    if (en[idx]) en[idx] = { ...en[idx], external: val }
    if (ar[idx]) ar[idx] = { ...ar[idx], external: val }
    onChange(en, ar)
  }

  return (
    <ItemListEditor
      title={title}
      itemsEn={itemsEn}
      itemsAr={itemsAr}
      onChange={onChange}
      makeEmpty={() => ({ en: { label: '', href: '' }, ar: { label: '', href: '' } })}
      addLabel="Add link"
    >
      {({ count, moveUp, moveDown, remove }) => (
        <div className="space-y-2">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-600">Link {i + 1}</span>
                <div className="flex items-center gap-1 text-xs">
                  <button type="button" onClick={() => moveUp(i)} className="px-2 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-40" disabled={i === 0}>↑</button>
                  <button type="button" onClick={() => moveDown(i)} className="px-2 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-40" disabled={i === count - 1}>↓</button>
                  <button type="button" onClick={() => remove(i)} className="px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">Remove</button>
                </div>
              </div>
              <BilingualTextField
                label="Label"
                path={`footer.${listKey}.${i}.label`}
                getField={getField} setField={setField}
              />
              <UrlField
                label="URL (shared across languages)"
                value={(itemsEn[i] && itemsEn[i].href) || ''}
                onChange={(v) => updateHref(i, v)}
                placeholder="/path or https://..."
              />
              {allowExternal && (
                <label className="inline-flex items-center gap-2 text-xs text-gray-700 mt-1">
                  <input
                    type="checkbox"
                    checked={!!(itemsEn[i] && itemsEn[i].external)}
                    onChange={(e) => toggleExternal(i, e.target.checked)}
                  />
                  Open in new tab (external)
                </label>
              )}
            </div>
          ))}
        </div>
      )}
    </ItemListEditor>
  )
}

export default function FooterSection(props) {
  const { getField, setField, draftEn, draftAr, setSubtree } = props

  const setBoth = (key, val) => {
    setField('en', key, val)
    setField('ar', key, val)
  }

  return (
    <SectionPanel title="Footer" subtitle="Column headings, link lists, newsletter, social URLs">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Column headings</h4>
      <BilingualTextField label='"Links" column' path="footer.columns.links" getField={getField} setField={setField} />
      <BilingualTextField label='"Legal" column' path="footer.columns.legal" getField={getField} setField={setField} />
      <BilingualTextField label='"Resources" column' path="footer.columns.resources" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />
      <LinkListEditor title="Links column" listKey="linksItems" {...props} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-1">Legal column and copyright</h4>
      <p className="text-xs text-gray-500 mb-2">
        Fixed in code: Pricing, Terms, Privacy, Refund Policy and Contact, plus the line
        &ldquo;© Stirring Apps FZE LLC&rdquo; with the licence number and address. They must match the trade licence.
      </p>

      <hr className="my-3 border-gray-200" />
      <LinkListEditor title="Resources column" listKey="resourcesItems" {...props} allowExternal />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Newsletter</h4>
      <BilingualTextField label="Newsletter title" path="footer.newsletter.title" getField={getField} setField={setField} />
      <BilingualTextField label="Newsletter subtitle" path="footer.newsletter.subtitle" getField={getField} setField={setField} />
      <BilingualTextField label="Email placeholder" path="footer.newsletter.placeholder" getField={getField} setField={setField} />
      <BilingualTextField label="Subscribe button label" path="footer.newsletter.submit" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Social URLs (shared across languages)</h4>
      <UrlField label="Facebook"  value={getField('en', 'brand.facebookUrl',  '')} onChange={(v) => setBoth('brand.facebookUrl',  v)} placeholder="https://facebook.com/..." />
      <UrlField label="Twitter / X" value={getField('en', 'brand.twitterUrl',   '')} onChange={(v) => setBoth('brand.twitterUrl',   v)} placeholder="https://twitter.com/..." />
      <UrlField label="Instagram" value={getField('en', 'brand.instagramUrl', '')} onChange={(v) => setBoth('brand.instagramUrl', v)} placeholder="https://instagram.com/..." />
      <UrlField label="LinkedIn"  value={getField('en', 'brand.linkedinUrl',  '')} onChange={(v) => setBoth('brand.linkedinUrl',  v)} placeholder="https://linkedin.com/company/..." />
    </SectionPanel>
  )
}
